
class IntegratorConfigDetailViewController extends MUIViewController
{
    static newInstance(): IntegratorConfigDetailViewController {
        let vc = new IntegratorConfigDetailViewController("integrator-config-detail-view");
        vc.initWithResource("layout/setup/IntegratorConfigDetailView.html");
        return vc;
    }

    private addButton: MUIButton = null;
    private backButton: MUIButton = null;
    private saveButton: MUIButton = null;
    private titleLabel: MUILabel = null;
    private nameTextField: MUITextField = null;
    private sourceEntityTextField: MUITextField = null;
    private filterTextField: MUITextField = null;
    private typeComboBox: MUIComboBox = null;
    private stopOnErrorSwitch: MUISwitchButton = null;
    private tableView: UITableView = null;

    private _config: IntegratorConfig = null;
    set config(item: IntegratorConfig) {
        this._config = item;
        this.updateUI();
    }

    // Set by the integrator detail before the config, so the settings of an email sender
    // can be shown even on configs created before they were seeded
    private _integratorType: string = null;
    set integratorType(value: string) {
        this._integratorType = value;
    }

    viewDidLoad() {
        super.viewDidLoad();

        this.backButton = MUIOutlet(this, "back-btn", "MUIButton");
        this.backButton.setAction(this, function() {
            this.navigationController.popViewController(true);
        });

        this.saveButton = MUIOutlet(this, "save-btn", "MUIButton");
        this.saveButton.setAction(this, function() {
            DBHelper.saveMainContext();
        });

        this.titleLabel = MUIOutlet(this, "title-lbl", "MUILabel");

        this.nameTextField = MUIOutlet(this, "name-tf", "MUITextField");
        this.nameTextField.setOnChangeText(this, function(tf: MUITextField, value: string) {
            if (this._config != null) {
                this._config.name = value;
            }
        });

        // The changelog subscription: which entity's changes feed this job through the
        // hook (ArchivedDocument for a feed, CashDeskSession for a shipper). EMPTY for
        // live jobs (room charge) — they are driven by their endpoint, and a stray value
        // here both generates junk syncs and makes health read the config as a queue.
        this.sourceEntityTextField = MUIOutlet(this, "source-entity-tf", "MUITextField");
        this.sourceEntityTextField.setOnChangeText(this, function(tf: MUITextField, value: string) {
            if (this._config != null) this._config.sourceEntityName = value.trim();
        });

        this.filterTextField = MUIOutlet(this, "filter-tf", "MUITextField");
        this.filterTextField.setOnChangeText(this, function(tf: MUITextField, value: string) {
            this._config.filter = value.trim().length > 0 ? value.trim() : null;
        });

        // Sync mode: manual = user pushes each sync, semi-automatic = queued for the
        // user to confirm, automatic = ships on its own.
        this.typeComboBox = MUIOutlet(this, "type-cb", "MUIComboBox");
        this.typeComboBox.removeAllItems();
        this.typeComboBox.addItem(MIOLocalizeString("MANUAL", "Manual"), 0);
        this.typeComboBox.addItem(MIOLocalizeString("SEMI_AUTOMATIC", "Semi-automatic"), 1);
        this.typeComboBox.addItem(MIOLocalizeString("AUTOMATIC", "Automatic"), 2);
        this.typeComboBox.setOnChangeAction(this, function(this:IntegratorConfigDetailViewController, cb: MUIComboBox, value: string) {
            this._config.type = parseInt(value);
        });

        // Queue error policy: ON parks the queue behind an errored sync until it is
        // retried (sequential contracts); OFF records the error and keeps shipping.
        // Chained integrations (TicketBAI-like) halt regardless — the server mandate
        // outranks this switch. NOTE: the model DEFAULTS to true on new configs.
        this.stopOnErrorSwitch = MUIOutlet(this, "stop-on-error-sw", "MUISwitchButton");
        if (this.stopOnErrorSwitch) this.stopOnErrorSwitch.setOnChangeValue(this, function(this:IntegratorConfigDetailViewController, control:MUISwitchButton, value:boolean) {
            if (this._config != null) this._config.stopOnError = value;
        });

        this.addButton = MUIOutlet(this, "add-btn", "MUIButton");
        this.addButton.setAction(this, function() {
            this.addConfigEntry();
        });

        this.tableView = MUIOutlet(this, "table-view", "UITableView");
        this.tableView.dataSource = this;
        this.tableView.delegate = this;

        this.updateUI();
    }

    viewWillAppear(animate?) {
        super.viewWillAppear(animate);
        this.updateUI();
    }

    private _configValues: any = null;
    private _configKeys: string[] = [];
    private _declaredSettings: any[] = [];

    private updateUI() {
        if (this.viewIsLoaded == false || this._config == null) return;

        this.titleLabel.text = this._config.name;
        DriverManifests.load(() => this.reloadRows());
        this.nameTextField.text = this._config.name;
        this.sourceEntityTextField.text = this._config.sourceEntityName;
        this.filterTextField.text = this._config.filter;
        this.typeComboBox.selectItem(this._config.type || 0);
        if (this.stopOnErrorSwitch) this.stopOnErrorSwitch.on = this._config.stopOnError == true;

        this.reloadRows();
    }

    // Row model: the settings this config's role declares (first, typed controls), then
    // the free key/value rows for everything the declaration doesn't cover. "role" is
    // the config's identity — shown on the list row, not editable here.
    private reloadRows() {
        if (this.viewIsLoaded == false || this._config == null) return;

        this._configValues = this._config.configValues || {};

        // Shown, not stored: the config is only written when one of them is edited, so
        // opening it never dirties an untouched config
        this._configValues = IntegratorEmailDefaults.seed(this._configValues, IntegratorEmailDefaults.senderKeys(this._integratorType));

        let role = this._configValues["role"];
        this._declaredSettings = DriverManifests.jobSettings(this._integratorType, role);

        let declared = this._declaredSettings.map((s: any) => s["key"]);
        this._configKeys = Object.keys(this._configValues).filter(k => k != "role" && declared.indexOf(k) < 0);

        this.tableView.reloadData();
    }

    numberOfSections(tableview) {
        return 1;
    }

    numberOfRowsInSection(tableview, section) {
        return this._declaredSettings.length + this._configKeys.length;
    }

    cellAtIndexPath(tableview, indexPath: MIOIndexPath) {
        if (indexPath.row < this._declaredSettings.length) {
            let setting = this._declaredSettings[indexPath.row];
            let identifier = setting["kind"] == "bool" ? "IntegratorConfigSettingSwitchCell" : "IntegratorConfigSettingCell";
            let scell = tableview.dequeueReusableCellWithIdentifier(identifier) as IntegratorConfigSettingCell;
            scell.setSetting(setting, this._config);
            return scell;
        }

        let cell = tableview.dequeueReusableCellWithIdentifier("IntegratorConfigKeyValueCell") as IntegratorConfigKeyValueCell;
        let key = this._configKeys[indexPath.row - this._declaredSettings.length];
        cell.setKeyValue(key, this._configValues[key], this._configValues, this._config);
        return cell;
    }

    didSelectCellAtIndexPath(tableView, indexPath: MIOIndexPath) {
        // selection handled inline via cell controls
    }

    editingStyleForRowAtIndexPath(tableView: UITableView, indexPath: MIOIndexPath) {
        // Declared settings belong to the role — they can be emptied, not removed.
        if (indexPath.row < this._declaredSettings.length) return UITableViewCellEditingStyle.None;
        return UITableViewCellEditingStyle.Delete;
    }

    commitEditingStyleForRowAtIndexPath(tableView, editingStyle: UITableViewCellEditingStyle, indexPath: MIOIndexPath) {
        if (editingStyle == UITableViewCellEditingStyle.Delete) {
            let row = indexPath.row - this._declaredSettings.length;
            if (row < 0) return;
            let key = this._configKeys[row];
            delete this._configValues[key];
            this._configKeys.splice(row, 1);
            this._config.configValues = this._configValues;
            this.tableView.reloadData();
        }
    }

    private addConfigEntry() {
        let avc = new MUIAlertViewController();
        avc.initWithTitle(
            MIOLocalizeString("NEW_CONFIG_ENTRY", "NEW CONFIG ENTRY"),
            MIOLocalizeString("ENTER_KEY_AND_VALUE", "ENTER KEY AND VALUE"),
            MUIAlertViewStyle.Default
        );

        avc.addTextFieldWithConfigurationHandler(this, function(textField: MUITextField) {
            textField.setPlaceholderText(MIOLocalizeString("KEY", "KEY"));
        });

        avc.addTextFieldWithConfigurationHandler(this, function(textField: MUITextField) {
            textField.setPlaceholderText(MIOLocalizeString("VALUE", "VALUE"));
        });

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("ADD", "ADD"), MUIAlertActionStyle.Default, this, function() {
            let key = avc.textFields[0].text.trim();
            let value = avc.textFields[1].text.trim();
            if (key.length > 0) {
                this._configValues[key] = value;
                this._configKeys = Object.keys(this._configValues);
                this._config.configValues = this._configValues;
                this.tableView.reloadData();
            }
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);
    }
}
